// Landing page after the login API refuses an unverified user.
//
// The email is carried in the route state from LoginPage / RegisterPage.
// If the user lands here cold (e.g. clicked an external link, or refreshed
// the tab), they can type the email manually to request a fresh link.
//
// Resend honors the per-user exponential backoff + 5/day cap from the
// service layer. The endpoint returns 200 even on rate-limit so we don't
// leak whether the email is registered; we surface the cooldown via the
// `rate_limited` flag in the response body.

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/stores/toastStore";

interface SendResponse {
  message: string;
  rate_limited?: boolean;
  retry_after_seconds?: number;
  reason?: string;
  // Set when our upstream provider (Resend) refused or failed to
  // dispatch — token slot was still consumed, so the per-user cooldown
  // applies, but the email never went out. Frontend shows a contact-
  // support hint instead of a misleading success toast.
  delivery_failed?: boolean;
}

// Optimistic floor cooldown after a successful send. Matches the
// backend's first-attempt cooldown (2^0 = 1 minute). Stops rapid
// re-clicks burning attempts before the server even sees them; if the
// real cooldown is longer (later attempts grow exponentially), the
// next click will get the true retry_after_seconds back from the API.
const OPTIMISTIC_COOLDOWN_SECONDS = 60;

function formatCountdown(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

export function VerifyEmailRequiredPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useDict().app.verifyRequired;
  const accessToken = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);
  const userEmail = useAuthStore((s) => s.user?.email);
  // Email is read-only here. We pull it from (in priority order) the
  // route state passed by LoginPage / RegisterPage, then the cached
  // user object. If we have neither, the user landed cold (e.g.
  // pasted the URL or refreshed without state) — we hide the resend
  // form entirely and tell them to sign in instead. Letting them
  // type any email turns this page into an enumeration tool.
  const stateEmail =
    (location.state as { email?: string } | null)?.email ?? userEmail ?? null;

  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  // Absolute wall-clock ms when the resend button becomes clickable
  // again, or null if not locked. Stored as a deadline (not a remaining-
  // seconds counter) so the timer stays accurate across tab-throttling
  // and lost setInterval ticks.
  const [lockedUntilMs, setLockedUntilMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  // Tick once per second while locked so the countdown label updates.
  // Self-clears the lock when the deadline passes.
  useEffect(() => {
    if (lockedUntilMs == null) return;
    const id = window.setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (now >= lockedUntilMs) {
        setLockedUntilMs(null);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [lockedUntilMs]);

  const remainingSeconds =
    lockedUntilMs != null
      ? Math.max(0, Math.ceil((lockedUntilMs - nowMs) / 1000))
      : 0;
  const isLocked = remainingSeconds > 0;

  async function resend() {
    if (!stateEmail || busy || isLocked) return;
    setBusy(true);
    try {
      const r = await api<SendResponse>("/auth/email/send-verification", {
        method: "POST",
        body: { email: stateEmail },
      });
      if (r.rate_limited) {
        const retryAfter = r.retry_after_seconds ?? 0;
        if (retryAfter > 0) {
          setLockedUntilMs(Date.now() + retryAfter * 1000);
        }
        // A rate_limited response means the server already has a
        // recent token for this user — i.e. an email is already on
        // its way (or was, recently). Flip `sent` so the post-refresh
        // case (state lost, click → rate_limited) shows the right
        // copy ("We sent a link to ...") instead of the initial
        // "click below to send" copy.
        setSent(true);
        toast.info(
          fmt(t.tooSoon, { t: formatCountdown(retryAfter) }),
          6000,
        );
      } else if (r.delivery_failed) {
        // Backend minted a token but Resend refused. Lock the button
        // for the optimistic cooldown anyway (the slot was consumed)
        // and show a persistent contact-support banner — the toast
        // alone would disappear before the user can act on it.
        setDeliveryFailed(true);
        setLockedUntilMs(Date.now() + OPTIMISTIC_COOLDOWN_SECONDS * 1000);
        toast.error(
          t.cantSend,
          8000,
        );
      } else {
        setSent(true);
        setDeliveryFailed(false);
        // Optimistic lock so rapid re-clicks don't burn attempts
        // before the server's exponential cooldown kicks in.
        setLockedUntilMs(Date.now() + OPTIMISTIC_COOLDOWN_SECONDS * 1000);
        toast.success(t.freshLink);
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : t.couldNotRequest,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-5">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TrueHacking" className="h-20 w-auto mb-4 mx-auto" />
          <div className="font-display text-[32px] font-bold text-accent tracking-[0.14em]">
            TRUEHACKING
          </div>
          <div className="font-mono text-[10px] text-dim tracking-[0.1em] mt-1 uppercase">
            {t.vrTitle}
          </div>
        </div>

        <Card className="!p-5 sm:!p-7">
          {stateEmail ? (
            <>
              <div className="font-display text-[18px] font-semibold text-content mb-2">
                {t.oneMoreStep}
              </div>
              <div className="font-mono text-[11px] text-muted leading-[1.6] mb-5">
                {sent ? (
                  <>
                    {t.sentPre}{" "}
                    <span className="text-accent break-all">{stateEmail}</span>
                    {t.sentPost}
                  </>
                ) : (
                  <>
                    {t.unsentPre}{" "}
                    <span className="text-accent break-all">{stateEmail}</span>
                    {t.unsentPost}
                  </>
                )}
              </div>

              <Button
                variant="primary"
                full
                onClick={() => void resend()}
                disabled={busy || isLocked}
              >
                {busy
                  ? t.sending
                  : isLocked
                    ? fmt(t.resendIn, { t: formatCountdown(remainingSeconds) })
                    : sent
                      ? t.resendEmail
                      : t.sendEmail}
              </Button>

              {deliveryFailed && (
                <div className="mt-3 px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] leading-[1.6]">
                  {t.deliveryFailedPre}{" "}
                  <a
                    href="mailto:support@truehacking.ai"
                    className="text-accent underline"
                  >
                    support@truehacking.ai
                  </a>
                  {t.deliveryFailedPost}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Cold landing — no email in state, no cached user. We
                  refuse to accept arbitrary input here because it would
                  turn this page into an enumeration probe. */}
              <div className="font-display text-[18px] font-semibold text-content mb-2">
                {t.vrTitle}
              </div>
              <div className="font-mono text-[11px] text-muted leading-[1.6] mb-5">
                {t.coldBody}
              </div>
              <Button
                variant="primary"
                full
                onClick={() => navigate("/login", { replace: true })}
              >
                {t.goSignIn}
              </Button>
            </>
          )}

          {/* "Sign out" is the right action here — the user is technically
              authed (registration issued tokens) but locked out of /app
              by the verification gate. To use a different account they
              need to drop the current session. Bare "back to sign in"
              would bounce back here via RedirectIfAuthed since they're
              still authed-but-unverified. */}
          <div className="mt-4 text-center">
            {accessToken ? (
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate("/login", { replace: true });
                }}
                className="bg-transparent border-none text-accent font-mono text-[11px] underline cursor-pointer"
              >
                {t.signOutOther}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="bg-transparent border-none text-accent font-mono text-[11px] underline cursor-pointer"
              >
                {t.backToSignin}
              </button>
            )}
          </div>

          <div className="font-mono text-[10px] text-dim leading-[1.6] mt-6 pt-4 border-t border-stroke">
            {t.hintPre}{" "}
            <span className="text-accent break-all">noreply@truehacking.ai</span>
            {t.hintMid}{" "}
            <a
              href="mailto:support@truehacking.ai"
              className="text-accent underline"
            >
              support@truehacking.ai
            </a>
            {t.hintPost}
          </div>
        </Card>
      </div>
    </div>
  );
}
